import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Section } from './ui';

/**
 * Shared inline-invites list used by both Home and Notifications. Renders a
 * "My Invites (N)" Section with per-row Accept / Decline for pending group,
 * course, practice, and event invites. Returns null when there are 0 pending.
 *
 * Practice-invite accept follows the paid-content rule (design/022): free
 * practices auto-enroll; paid practices redirect the user to Stripe checkout.
 *
 * @param {object} props
 * @param {Array}  props.groupInvites     — api.listMyInvites()
 * @param {Array}  props.courseInvites    — api.listMyCourseInvites()
 * @param {Array}  [props.practiceInvites]— api.listMyPracticeInvites()
 * @param {Array}  [props.eventInvites]   — api.listMyEventInvites()
 * @param {Function} props.onChanged      — reload callback after accept/decline
 * @param {string} [props.titlePrefix]    — override the "My Invites" label
 */
export default function InvitesSection({
  groupInvites = [], courseInvites = [], practiceInvites = [], eventInvites = [],
  onChanged, titlePrefix = 'My Invites',
}) {
  const total = groupInvites.length + courseInvites.length + practiceInvites.length + eventInvites.length;
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();
  if (total === 0) return null;

  const acceptGroup = async (inv) => {
    setBusy(`g:${inv.token || inv.id}`);
    try {
      const r = await api.acceptInvite(inv.token);
      if (r?.requiresCheckout && r.checkoutUrl) {
        window.location.assign(r.checkoutUrl);
      } else if (inv.group?.id) {
        navigate(`/group/${inv.group.id}`);
      } else {
        await onChanged?.();
      }
    } catch (e) { alert(e.message); }
    finally { setBusy(null); }
  };
  const declineGroup = async (inv) => {
    if (!window.confirm('Decline this invite?')) return;
    setBusy(`g:${inv.token || inv.id}`);
    try { await api.declineInvite(inv.token); await onChanged?.(); }
    catch (e) { alert(e.message); }
    finally { setBusy(null); }
  };
  const acceptCourse = async (inv) => {
    setBusy(`c:${inv.id}`);
    try {
      const r = await api.acceptCourseInvite(inv.shortCode);
      // V61 — paid course not made free for you → Stripe checkout.
      if (r?.requiresCheckout && r.checkoutUrl) window.location.assign(r.checkoutUrl);
      else if (inv.courseId) navigate(`/learn/course/${inv.courseId}`);
      else await onChanged?.();
    } catch (e) { alert(e.message); }
    finally { setBusy(null); }
  };
  const declineCourse = async (inv) => {
    if (!window.confirm('Decline this invite?')) return;
    setBusy(`c:${inv.id}`);
    try { await api.declineCourseInvite(inv.shortCode); await onChanged?.(); }
    catch (e) { alert(e.message); }
    finally { setBusy(null); }
  };
  const acceptPractice = async (inv) => {
    setBusy(`p:${inv.id}`);
    try {
      const r = await api.acceptPracticeInvite(inv.shortCode);
      // Paid practice → server sends a Stripe URL; free → navigate to the practice.
      if (r?.requiresCheckout && r.checkoutUrl) {
        window.location.assign(r.checkoutUrl);
      } else if (r?.practiceId) {
        navigate(`/learn/practice/${r.practiceId}`);
      } else {
        await onChanged?.();
      }
    } catch (e) { alert(e.message); }
    finally { setBusy(null); }
  };
  const declinePractice = async (inv) => {
    if (!window.confirm('Decline this invite?')) return;
    setBusy(`p:${inv.id}`);
    try { await api.declinePracticeInvite(inv.shortCode); await onChanged?.(); }
    catch (e) { alert(e.message); }
    finally { setBusy(null); }
  };
  const acceptEvent = async (inv) => {
    setBusy(`e:${inv.id}`);
    try {
      const r = await api.acceptEventInvite(inv.shortCode);
      if (r?.requiresCheckout && r.checkoutUrl) {
        window.location.assign(r.checkoutUrl);
      } else if (r?.eventId) {
        navigate(`/event/${r.eventId}`);
      } else {
        await onChanged?.();
      }
    } catch (e) { alert(e.message); }
    finally { setBusy(null); }
  };
  const declineEvent = async (inv) => {
    if (!window.confirm('Decline this invite?')) return;
    setBusy(`e:${inv.id}`);
    try { await api.declineEventInvite(inv.shortCode); await onChanged?.(); }
    catch (e) { alert(e.message); }
    finally { setBusy(null); }
  };

  return (
    <Section title={`${titlePrefix} (${total})`}>
      {courseInvites.map((inv) => {
        const key = `c:${inv.id}`;
        return (
          <div key={key} className="member-row">
            <div style={{ flex: 1 }}>
              <div className="nm">{inv.courseTitle || 'A course'}<MyInvitePrice inv={inv} /></div>
              <div className="em">course invite from {inv.inviterName || 'a publisher'}</div>
            </div>
            <button className="btn btn-sm" disabled={busy === key} onClick={() => declineCourse(inv)}>Decline</button>
            <button className="btn btn-sm btn-primary" disabled={busy === key} onClick={() => acceptCourse(inv)}>
              {busy === key ? '…' : 'Accept & enroll'}
            </button>
          </div>
        );
      })}
      {practiceInvites.map((inv) => {
        const key = `p:${inv.id}`;
        return (
          <div key={key} className="member-row">
            <div style={{ flex: 1 }}>
              <div className="nm">{inv.practiceTitle || 'A practice'}<MyInvitePrice inv={inv} /></div>
              <div className="em">practice invite from {inv.inviterName || 'a publisher'}</div>
            </div>
            <button className="btn btn-sm" disabled={busy === key} onClick={() => declinePractice(inv)}>Decline</button>
            <button className="btn btn-sm btn-primary" disabled={busy === key} onClick={() => acceptPractice(inv)}>
              {busy === key ? '…' : 'Accept'}
            </button>
          </div>
        );
      })}
      {eventInvites.map((inv) => {
        const key = `e:${inv.id}`;
        return (
          <div key={key} className="member-row">
            <div style={{ flex: 1 }}>
              <div className="nm">{inv.eventTitle || 'An event'}<MyInvitePrice inv={inv} /></div>
              <div className="em">event invite from {inv.inviterName || 'someone'}</div>
            </div>
            <button className="btn btn-sm" disabled={busy === key} onClick={() => declineEvent(inv)}>Decline</button>
            <button className="btn btn-sm btn-primary" disabled={busy === key} onClick={() => acceptEvent(inv)}>
              {busy === key ? '…' : 'Accept'}
            </button>
          </div>
        );
      })}
      {groupInvites.map((inv) => {
        const key = `g:${inv.token || inv.id}`;
        return (
          <div key={key} className="member-row">
            <div style={{ flex: 1 }}>
              <div className="nm">{inv.group?.name || inv.groupName || 'A group'}<MyInvitePrice inv={inv} /></div>
              <div className="em">
                group invite{inv.inviterUsername ? ` from @${inv.inviterUsername}` : ''}
              </div>
            </div>
            <button className="btn btn-sm" disabled={busy === key} onClick={() => declineGroup(inv)}>Decline</button>
            <button className="btn btn-sm btn-primary" disabled={busy === key} onClick={() => acceptGroup(inv)}>
              {busy === key ? '…' : 'Accept'}
            </button>
          </div>
        );
      })}
    </Section>
  );
}

/** Invitee side: "Free for you (normally $22.00)" or "$22.00 — you pay when you accept". */
function MyInvitePrice({ inv }) {
  const price = inv.priceCents || 0;
  if (price <= 0) return null;
  const money = `$${(price / 100).toFixed(2)}`;
  return inv.freeAccess
    ? <span className="pill" style={{ marginLeft: 6, background: '#dcfce7', color: '#166534' }}>Free for you (normally {money})</span>
    : <span className="pill" style={{ marginLeft: 6, background: '#fef3c7', color: '#92400e' }}>{money} — you pay when you accept</span>;
}
